import { Control } from "react-hook-form";
import { Toggle } from "@carbon/react";
import i18next from "../../../i18n";
import { CertificationRequest, TagResponse } from "../../../types";
import TagPicker from "../../../components/carbon/TagPicker/TagPicker";
import TextField from "../../../components/carbon/Form/TextField";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import MaterialCertifications from "../../../components/NewMaterialForm/MaterialCertifications";
import type { NewMaterialModalValues } from "./newMaterialModalSchema";

export type NewMaterialAdvancedProps = {
  control: Control<NewMaterialModalValues>;
  isService: boolean;
  tags: TagResponse[];
  onTagsChange: (tags: TagResponse[]) => void;
  certifications: CertificationRequest[];
  onCertificationsChange: (certifications: CertificationRequest[]) => void;
  materialConfidential: boolean;
  onMaterialConfidentialChange: (confidential: boolean) => void;
};

/**
 * The "Más opciones" (advanced) section of the create-material modal — brand, delivery format
 * (goods only), tags, comments, certifications and the material-confidentiality toggle. Split out of
 * `NewMaterialModal` purely to keep each file under the 400-line budget. `brand`/`deliveryFormat`/
 * `comments` are clean RHF fields; `tags`/`certifications`/`materialConfidential` stay parent-owned
 * local state (they never had a Yup rule, before or after the migration) and reuse the same
 * `TagPicker`/`MaterialCertifications` widgets the standalone create-material page uses.
 */
const NewMaterialAdvanced = ({
  control,
  isService,
  tags,
  onTagsChange,
  certifications,
  onCertificationsChange,
  materialConfidential,
  onMaterialConfidentialChange,
}: NewMaterialAdvancedProps) => {
  return (
    <div className="new-material-modal__advanced">
      <div className="new-material-modal__grid">
        <TextField
          name="brand"
          label={i18next.t("newMaterialForm.brand")}
          control={control}
          optional
        />
        {!isService && (
          <TextField
            name="deliveryFormat"
            label={i18next.t("newMaterialForm.deliveryFormat")}
            control={control}
            optional
          />
        )}
      </div>

      <div className="new-material-modal__field">
        <TagPicker
          selectedTags={tags}
          onChange={onTagsChange}
          label={i18next.t("tags.tags")}
          allowCreate
          maxTags={10}
        />
      </div>

      <TextareaField
        name="comments"
        label={i18next.t("purchaseRequests.fields.comments")}
        control={control}
        optional
        rows={2}
      />

      <MaterialCertifications
        certifications={certifications}
        onChange={onCertificationsChange}
      />

      <Toggle
        id="material-confidential"
        labelText={i18next.t("newMaterialForm.confidentialMaterial")}
        toggled={materialConfidential}
        onToggle={onMaterialConfidentialChange}
      />
    </div>
  );
};

export default NewMaterialAdvanced;
